<template>
  <Loader v-if="loading" />
  <div v-else class="content">
    <template v-if="!data.subscription">
      <p>Customer does not have a subscription</p>

      <button
        :disabled="saving"
        v-on:click="createSubscription(2)"
        class="small-margin-right"
      >
        Create 2 month subscription
      </button>
      <button :disabled="saving" v-on:click="createSubscription(3)">
        Create 3 month subscription
      </button>
    </template>
    <template v-else>
      <Modal v-if="show_cancellation" v-on:close="show_cancellation = false">
        <div class="small-margin-bottom">
          <strong>Cancellation reason</strong>
        </div>
        <select v-model="cancellation_reason">
          <option
            :value="reason.id"
            v-for="reason in data.cancellation_reasons"
            :key="reason.id"
          >
            {{ reason.reason }}
          </option>
        </select>

        <div class="margin-top" v-show="cancellation_reason">
          <textarea
            v-model="cancellation_feedback"
            rows="4"
            cols="60"
          ></textarea>
        </div>

        <div class="margin-top">
          <MaterialToggle
            v-model="cancel_orders"
            label="Cancel styling orders not submitted to warehouse"
          />
        </div>

        <div class="margin-top">
          <button
            :disabled="saving"
            v-on:click="cancelSubscription()"
            class="danger"
          >
            Confirm
          </button>
        </div>
      </Modal>

      <div class="flex">
        <div class="flex-grow text-right">
          <template v-if="!data.subscription.cancelled">
            <button
              :disabled="saving"
              class="white small small-margin-left"
              v-on:click="show_cancellation = true"
              v-if="!data.subscription.cancelled_at"
            >
              Cancel
            </button>
            <button
              :disabled="saving"
              class="white small small-margin-left"
              v-on:click="pause()"
            >
              Pause
              <span class="info"
                >The renewal date will be changed to a select number of months
                from today's date</span
              >
            </button>
            <button
              :disabled="saving"
              class="white small small-margin-left"
              v-on:click="skipNextBox()"
            >
              Skip next box
              <span class="info">
                The renewal date will be changed to current renewal date ({{
                  data.subscription.next_renewal_at
                }}) + {{ data.subscription.interval }} months
              </span>
            </button>
            <button
              :disabled="saving"
              class="white small small-margin-left"
              v-on:click="renew()"
            >
              Renew now
              <span class="info">
                A new styling will be created now, and the renewal date will be
                set to today's date +
                {{ data.subscription.interval }} months
              </span>
            </button>

            <button
              v-if="data.subscription.failed_payment_state"
              :disabled="saving"
              class="white small small-margin-left"
              v-on:click="expireFailedPayment()"
            >
              Expire failed payment state
              <span class="info">
                We will no longer attempt to capture the subscription, and
                instead push it to the next renewal day. This normally happens
                automatically after 7 days
              </span>
            </button>

            <button
              :disabled="saving"
              v-on:click="putOnHold()"
              class="white small small-margin-left"
              v-if="!data.subscription.on_hold"
            >
              Put on hold
              <span class="info">
                Subscription wil not be renewed until it's released from holding
                position
              </span>
            </button>
            <button
              :disabled="saving"
              v-on:click="releaseFromHold()"
              class="white small small-margin-left"
              v-if="data.subscription.on_hold"
            >
              Release from hold
            </button>
          </template>

          <template v-if="!!data.subscription.cancelled_at">
            <button
              :disabled="saving"
              v-on:click="uncancel()"
              class="white small small-margin-left"
            >
              Uncancel
            </button>
          </template>
        </div>
      </div>
      <div class="large-margin-top">
        <div class="flex">
          <div class="flex-basis">
            <div class="input-row small-margin-bottom">
              <span class="label">ID</span>
              {{ data.subscription.id }}
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Customer</span>
              <CustomerLink :customer="data.customer" />
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Renewing on order</span>
              <div v-if="data.subscription.renewing_on_order">
                <OrderLink :id="data.subscription.renewing_on_order.id" />
              </div>
              <div v-else>Not associated</div>
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Renewal style</span>
              {{
                data.has_automatic_renewal
                  ? "Automatic (Credit card)"
                  : "Manual (iDeal, Klarna, etc.)"
              }}
            </div>
            <div
              class="input-row small-margin-bottom"
              v-if="!data.has_automatic_renewal"
            >
              <span class="label">Manual renewal</span>
              <div v-if="data.manual_renewal" class="success">
                <template v-if="data.manual_renewal.reminder_2_at">
                  Second email sent @
                  <Format
                    type="date"
                    :time="true"
                    :value="data.manual_renewal.reminder_2_at"
                  />
                </template>
                <template v-else-if="data.manual_renewal.reminder_1_at">
                  First email sent @
                  <Format
                    type="date"
                    :time="true"
                    :value="data.manual_renewal.reminder_1_at"
                  />
                </template>
                <template v-else> Communication will initiate soon! </template>
              </div>
              <div v-else class="warning">Not in renewal</div>
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Active*</span>
              <div v-if="data.subscription.active">Yes</div>
              <div v-else>No</div>
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">On hold*</span>
              <div v-if="data.subscription.on_hold">Yes</div>
              <div v-else>No</div>
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Start date</span>
              <Format
                type="date"
                :time="true"
                :value="data.subscription.start_date"
              />
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Next renewal</span>
              <Format
                type="date"
                :time="true"
                :value="data.subscription.next_renewal_at"
              />
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Last renewed</span>
              <Format
                type="date"
                :time="true"
                :value="data.subscription.last_renewed_at"
              />
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Renewal interval</span>
              Every {{ data.subscription.interval }} months
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Renewal URL</span>
              <a
                target="_blank"
                v-if="data.renewal_url"
                :href="data.renewal_url"
                >{{ data.renewal_url.replace(/^https?:\/\/[^\/]+\//, "") }}</a
              >
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Next box</span>
              <div class="flex">
                <div>
                  {{ data.customer.locale.currency }} {{ renewal_price }}
                </div>
              </div>
            </div>
            <div
              class="input-row small-margin-bottom"
              v-if="renewal_price != default_renewal_price.price"
            >
              <span class="label">Default renewal price</span>
              <div class="flex">
                <div>
                  {{ data.customer.locale.currency }}
                  {{ default_renewal_price.price }}
                </div>
              </div>
            </div>
            <div class="input-row small-margin-bottom">
              <span class="label">Cancelled</span>
              <Format
                type="date"
                :time="true"
                :value="data.subscription.cancelled_at"
              />
            </div>
            <div class="input-row small-margin-bottom">
              <div class="label">Failed</div>
              <div>
                <template v-if="data.subscription.failed_payment_state">
                  First failed at:
                  <Format
                    type="date"
                    :value="data.subscription.failed_first_at"
                    :time="true"
                  />
                  <br />
                  Latest capture attempt at:
                  <Format
                    type="date"
                    :value="data.subscription.failed_retried_at"
                    :time="true"
                  />
                  <br />
                  <div>Order:</div>
                  <OrderLink :id="data.subscription.failed_order_id" />
                </template>
                <template v-else> Not in failed payment state </template>
              </div>
            </div>

            <div class="note large-margin-top width80pct">
              *) A subscription can be active and on hold at the same time. The
              active status is related to whether the subscription is cancelled
              or not. Being "on hold" is merely a 'carte blanche' pause that may
              be enabled for any reason.
            </div>
          </div>
          <div class="flex-basis" v-if="data.subscription.active">
            <Group title="Next renewal" class="margin-bottom">
              <div class="flex">
                <!-- <DateTimePicker type="date" v-model="data.subscription.next_renewal_at" /> -->
                <input type="text" v-model="next_renewal_at" />
                <span style="padding: 10px;">earliest renewal date: {{ earliest_renewal_at }}</span>
                <div class="flex-grow text-right">
                  <button
                    class="small white"
                    :disabled="saving"
                    v-on:click="updateNextRenewal()"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Group>
            <Group title="Renewal price (next box only)" class="margin-bottom">
              <div class="flex">
                <MaterialNumber
                  label="Price"
                  v-model="renewal_price"
                  :unit="data.customer.locale.currency"
                />
                <div class="flex-grow text-right">
                  <button
                    class="small white"
                    :disabled="saving"
                    v-on:click="updateNextPrice()"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Group>
            <!-- <Group title="Renewal interval">
              <div class="flex">
                <MaterialNumber
                  v-model="data.subscription.interval"
                  label="Every N months"
                  class="short"
                />
                <div class="flex-grow text-right">
                  <button
                    class="small white"
                    :disabled="saving"
                    v-on:click="updateInterval()"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Group> -->
            <Group title="Settings">
              <div class="flex">
                <MaterialNumber v-model="data.subscription.interval" label="Every N months" class="short" />
                <div class="flex-grow text-right">
                  <button class="small white" :disabled="saving" v-on:click="update">Save</button>
                </div>
              </div>
            </Group>
            <Group title="Reactivation" class="margin-top">
              <div class="flex">
                <MaterialToggle
                  v-model="can_reactivate"
                  label="Prevent reactivation" />
                <div class="flex-grow text-right very-small-margin-top">
                  <button class="small white" :disabled="saving" v-on:click="reactiveSubscription">Save</button>
                </div>
              </div>
            </Group>
          </div>
          <div class="flex-basis" v-else>
            <Group title="Reactivation" class="margin-top">
              <div class="flex">
                <MaterialToggle
                  v-model="can_reactivate"
                  label="Prevent reactivation" />
                  <div class="flex-grow text-right very-small-margin-top">
                    <button class="small white" :disabled="saving" v-on:click="reactiveSubscription">Save</button>
                  </div>
                </div>
              </Group>
          </div>
        </div>
      </div>

      <div v-if="data.events.length" class="margin-top">
        <h2>History</h2>

        <div
          v-for="event in data.events"
          class="flex small-margin-bottom"
          :key="event.id"
        >
          <div class="width15pct">
            <Format type="date" :time="true" :value="event.created_at" />
          </div>
          <div>
            <template v-if="event.event == 'put-on-hold'">
              Put on hold
            </template>
            <template v-else-if="event.event == 'released-from-hold'">
              Released from hold
            </template>
            <template v-else-if="event.event == 'changed-renewal-date'">
              Changed renewal date to {{ event.parameters.calculated_date.substr(0, 10) }}
            </template>
            <template v-else-if="event.event == 'shifted'">
              Shifted from {{ event.parameters.old_renewal_date.substr(0, 10) }} to {{ event.parameters.calculated_date.substr(0, 10) }}
              because of
              <OrderLink
                style="display: inline-block"
                :id="event.parameters.caused_by_order"
              />
              ({{ event.parameters.caused_reason }})
            </template>
            <template v-else-if="event.event == 'cancelled'">
              Cancelled
            </template>
            <template v-else-if="event.event == 'uncancelled'">
              Re-activated with renewal on
              <Format
                style="display: inline-block"
                type="date"
                :value="event.parameters.calculated_date.substr(0, 10)"
              />
            </template>
            <template v-else-if="event.event == 'changed-renewal-interval'">
              Changed renewal interval from {{ event.parameters.old_interval }} to
              {{ event.parameters.new_interval }} months
            </template>
            <template v-else-if="event.event == 'changed-renewal-price'">
              <template v-if="typeof event.parameter_1 === 'string'">
                Changed price of next box to
                {{ data.customer.locale.currency }} {{ event.parameter_1 }}
              </template>
              <template v-else>
                Set price of next box to system default
              </template>
            </template>
            <template v-else-if="event.event == 'created'">
              Subscription created
            </template>
            <template v-else-if="event.event == 'reset'">
              Subscription was reset (renewal price back to default, leaves
              failed payment state)
            </template>
            <template v-else-if="event.event == 'env-fee-on-future-orders' && Number(event.parameter_1)">
              Environment fee <strong>enabled</strong> for future orders
            </template>
            <template v-else-if="event.event == 'env-fee-on-future-orders' && !Number(event.parameter_1)">
              Environment fee <span class="red">disabled</span> for future orders
            </template>
            <template v-else-if="event.event == 'failed-renewal'">
              Failed to capture subscription:
              <div v-if="event.parameter_2">{{ event.parameter_2 }}</div>
            </template>
            <template v-else-if="event.event == 'failed-payment-state-entered'">
              Subscription was put in failed payment state:
              <div v-if="event.parameter_2">{{ event.parameter_2 }}</div>
            </template>
            <template v-else-if="event.event == 'failed-payment-state-expired'">
              Failed payment state expired
            </template>
            <template v-else-if="event.event == 'renewed'">
              Subscription was renewed on order
              <OrderLink
                style="display: inline-block"
                :id="event.parameters.order_id"
              />
            </template>
            <template v-else>
              {{ event.event }}
            </template>

            <template
              v-if="
                ['failed-payment-state-entered', 'failed-renewal'].indexOf(
                  event.event
                ) == -1
              "
            >
              by

              <template v-if="event.internal_user_id">
                <UserLink
                  style="display: inline-block"
                  :id="event.internal_user_id"
                />
              </template>
              <template v-else-if="event.by_customer">customer</template>
              <template v-else>system</template>
            </template>
          </div>
        </div>
      </div>
    </template>
    <Modal v-if="showErrorModal" v-on:close="showErrorModal = false">
      <span class="error">{{ errorMessage }}</span>
    </Modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

export default {
  props: ['customer', 'mode'],
  data: () => ({
    renewal_price: 0,
    default_renewal_price: 0,
    loading: false,
    saving: false,
    data: {},
    show_cancellation: false,
    cancellation_reason: false,
    cancel_orders: false,
    cancellation_feedback: '',
    next_renewal_at: '',
    earliest_renewal_at: '',
    showErrorModal: false,
    errorMessage: '',
    can_reactivate: false
  }),
  computed: {
    ...mapGetters(['getUsers'])
  },
  watch: {
    $route: function () {
      this.load()
    }
  },
  methods: {
    createSubscription (month) {
      var start_date = prompt(
        'Please enter the first renewal date (YYYY-MM-DD)',
        Vue.$create.dateTime().addDays(1).format('Y-m-d')
      )
      if (start_date) {
        let url = '/customer/' + this.customer + '/new-subscription'
        if (this.mode === 'testing') {
          url = '/customer/' + this.customer + '/new-subscription?testMode=1'
        }
        Vue.$api.post(
          url,
          {
            months: month,
            start_date: start_date
          },
          this.actionCallback()
        )
      }
    },
    expireFailedPayment () {
      Vue.$api.post(
        '/customer/' + this.customer + '/new-subscription/reset',
        {},
        this.actionCallback()
      )
    },
    renew (override) {
      Vue.$api.post(
        '/customer/' + this.customer + '/new-subscription/renew',
        {
          override: !!override
        },
        {
          loader: (toggle) => (this.saving = toggle),
          callback: data => this.afterRenew(data)
        }
      )
    },
    afterRenew (data) {
      if (data.prompt) {
        if (confirm(data.prompt)) {
          this.renew(true)
        }
        return
      }

      this.load()
    },
    updateNextRenewal () {
      Vue.$api.post(
        '/customer/' + this.customer + '/new-subscription/postpone',
        {
          date: this.next_renewal_at
        },
        {
          loader: (toggle) => (this.saving = toggle),
          callback: (data) => this.load()
        }
      )
    },
    updateNextPrice () {
      Vue.$api.patch(
        '/customer/' + this.customer + '/new-subscription/renewal-parameter',
        {
          price: this.renewal_price
        },
        {
          loader: (toggle) => (this.saving = toggle),
          callback: (data) => this.load()
        }
      )
    },
    updateInterval () {
      Vue.$api.put(
        '/customer/' + this.customer + '/new-subscription/interval',
        {
          interval: this.data.subscription.interval
        },
        this.actionCallback()
      )
    },
    update () {
      Vue.$api.patch('/customer/' + this.customer + '/new-subscription', {
        interval: this.data.subscription.interval,
        environment_fee_on_future_orders: this.data.customer_settings.environment_fee_on_future_orders
      }, this.actionCallback())
    },
    load () {
      if (!this.customer) {
        alert('Invalid Customer')
        return
      }
      Vue.$api.get(
        '/customer/' + this.customer + '/new-subscription',
        {},
        {
          loader: (toggle) => (this.loading = toggle),
          callback: (data) => {
            this.parse(data)
          },
          error: (err) => {
            if (err.data && err.data.message) {
              alert(err.data.message)
            }
          }
        }
      )
    },
    uncancel () {
      const nextPayment = prompt(
        'When should the next box be renewed (YYYY-MM-DD)? Leave empty for instantly'
      )

      Vue.$api.post(
        '/customer/' + this.customer + '/new-subscription/reactivate',
        {
          next_payment: nextPayment
        },
        {
          loader: (toggle) => (this.loading = toggle),
          callback: (data) => this.handleReactiveResponse(data)
        }
      )
    },
    putOnHold () {
      Vue.$api.post(
        '/customer/' + this.customer + '/new-subscription/on-hold',
        {},
        this.actionCallback()
      )
    },
    releaseFromHold () {
      Vue.$api.delete(
        '/customer/' + this.customer + '/new-subscription/on-hold',
        {},
        this.actionCallback()
      )
    },
    getId () {
      return this.customer
    },
    pause () {
      var months = prompt('How many months does the customer want to pause?')
      if (months > 0) {
        Vue.api.post(
          '/customer/' + this.getId() + '/new-subscription/pause',
          {
            months: months
          },
          this.actionCallback()
        )
      }
    },
    skipNextBox () {
      if (
        confirm('Are you sure we should skip the next box for this customer?')
      ) {
        Vue.api.post(
          '/customer/' + this.getId() + '/new-subscription/skip-next-box',
          {},
          this.actionCallback()
        )
      }
    },
    parse (data) {
      this.data = data

      if (data.renewal_price) {
        this.renewal_price = data.renewal_price.price
      }

      this.default_renewal_price = data.default_renewal_price
      if (data.earliest_renewal_date) {
        this.earliest_renewal_at = data.earliest_renewal_date
      }
      if (this.data.subscription) {
        if (this.data.subscription.next_renewal_at) {
          this.next_renewal_at = this.data.subscription.next_renewal_at.substr(
            0,
            10
          )
        }
      }
      if (this.data.subscription) {
        this.can_reactivate = this.data.subscription.can_reactivate === 0
      }
    },
    reactiveSubscription () {
      const reactivate = this.can_reactivate ? 0 : 1
      Vue.$api.get(
        '/new-subscriptions/' + this.data.subscription.id + '/set-can-reactivate' + '?can_reactivate=' + reactivate,
        {},
        {
          loader: (toggle) => (this.saving = toggle),
          callback: (data) => this.load()
        }
      )
    },
    cancelSubscription () {
      if (confirm('Are you sure?')) {
        Vue.api.post(
          '/customer/' + this.getId() + '/new-subscription/cancel',
          {
            reason: this.cancellation_reason,
            feedback: this.cancellation_feedback,
            cancel_orders: this.cancel_orders
          },
          this.actionCallback()
        )
      }
    },
    actionCallback () {
      return {
        loader: (toggle) => (this.saving = toggle),
        callback: (data) => {
          this.show_cancellation = false

          this.load()
        }
      }
    },
    handleReactiveResponse (data) {
      if (data.verified !== undefined && !data.verified) {
        this.showErrorModal = true
        if (
          data.errorMessage ===
          'credit-check.error-modal.verify.no-previous-check'
        ) {
          this.errorMessage =
            'The subscription cannot be uncancelled because there has not been done any ' +
            'credit check on customer. Please contact Thomas if subscription shall be uncancelled'
        }
        if (
          data.errorMessage === 'credit-check.error-modal.verify.not-verified'
        ) {
          this.errorMessage =
            'The subscription cannot be uncancelled because credit check for this customer is failed.'
        }
        if (
          data.errorMessage === 'credit-check.error-modal.verify.no-match-found'
        ) {
          this.errorMessage =
            'The subscription cannot be uncancelled because no credit info found for this customer.'
        }
      }
    }
  },
  created () {
    this.load()
  }
}
</script>
